import {useCallback, useEffect, useState} from "react";
import {getTypeConfig} from "./labelConfig";

/**
 * Charge / sauvegarde le modèle d'étiquette (format choisi + champs cochés) par type
 * dans localStorage, afin que les préférences soient restaurées d'une session à l'autre.
 *
 * @param {string} type - "product" | "delivery" | "client"
 * @returns {{format:string, selectedFields:string[], setFormat:Function, toggleField:Function, reset:Function}}
 */
export default function useLabelTemplate(type) {
    const config = getTypeConfig(type);
    const storageKey = `labelTemplate:${type}`;

    const readStored = useCallback(() => {
        try {
            const raw = localStorage.getItem(storageKey);
            if (!raw) return null;
            const parsed = JSON.parse(raw);
            // On ne garde que des champs encore valides pour ce type.
            const validIds = config.fields.map((f) => f.id);
            const selectedFields = Array.isArray(parsed.selectedFields)
                ? parsed.selectedFields.filter((id) => validIds.includes(id))
                : null;
            return {
                format: parsed.format || config.defaultFormat,
                selectedFields: selectedFields && selectedFields.length ? selectedFields : config.defaultSelected,
            };
        } catch {
            return null;
        }
    }, [storageKey, config]);

    const [format, setFormat] = useState(() => readStored()?.format || config.defaultFormat);
    const [selectedFields, setSelectedFields] = useState(
        () => readStored()?.selectedFields || config.defaultSelected
    );

    // Persiste à chaque changement.
    useEffect(() => {
        try {
            localStorage.setItem(storageKey, JSON.stringify({format, selectedFields}));
        } catch {
            /* quota / mode privé : on ignore silencieusement */
        }
    }, [storageKey, format, selectedFields]);

    const toggleField = useCallback((id) => {
        setSelectedFields((prev) =>
            prev.includes(id) ? prev.filter((f) => f !== id) : [...prev, id]
        );
    }, []);

    const reset = useCallback(() => {
        setFormat(config.defaultFormat);
        setSelectedFields(config.defaultSelected);
    }, [config]);

    return {format, selectedFields, setFormat, toggleField, reset};
}
